import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import R from 'ramda';
import { I13nAnchor, createI13nNode } from 'react-i13n';
import { Link as ReactRouterLink } from 'react-router-dom';

// These are props we will be using to detemine characteristics of the link
// and should not therefore not be passed to through to the child.
// We will omit these before passing all other props through to child.
const BLACKLIST_TO_CHILDREN_PROPS = [
  'className',
  'i13nModel',
  'i13nLabel',
  'testid',
  'newTab',
  'target',
  'rel'
];

const I13nLink = createI13nNode(
  ReactRouterLink,
  {
    isLeafNode: true,
    bindClickEvent: false,
    follow: true
  },
  { skipUtilFunctionsByProps: true }
);

class Link extends PureComponent {
  static propTypes = {
    /**
     * Only required prop. Passed directly as-is (no prefixing) as 'data-testid'.
     */
    testid: PropTypes.string.isRequired,
    /**
     * If 'href' passed in, link will represent an external link, utilizing
     * 'react-i13n's 'I13nAnchor' component. The 'newTab' prop is only relevant
     * in this scenario.
     */
    href: PropTypes.string,
    /**
     * When 'href' prop is passed in AND 'newTab' prop is true (default),
     * will apply target="_blank" and rel="noopener noreferrer" for security.
     */
    newTab: PropTypes.bool,
    /**
     * If passed in, link will represent an internal link, utilizing
     * 'react-router's 'Link' component. Can be the URL itself (string),
     * or an object with 'pathname', 'query', and/or 'hash' properties.
     */
    to: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        pathname: PropTypes.string,
        query: PropTypes.object,
        hash: PropTypes.string
      })
    ]),
    /**
     * Callback to fire when the link is clicked.
     */
    onClick: PropTypes.func,
    /**
     * Will be applied as the label field on the i13nModel if passed in, along
     * with default of action: 'click'. If neither i13nLabel or i13nModel are
     * passed in AND the child of the Link is text, label will default to
     * this text (most common scenario). Scenarios where you may not want this is
     * when the Link text is too generic - i.e. 'Disable' when there are many
     * 'Disable' buttons on the page. The label is always automatically prefixed
     * with what page of the dashboard the user is on so this information does
     * not need to be included in the label.
     */
    i13nLabel: PropTypes.string,
    /**
     * Any properties passed on the i13nModel prop will overwrite the
     * corresponding default property: action of 'click',
     * and label set to the text on the Link (if it is text).
     * For example,
     * if the link text is 'Cancel' but you wanted to overwrite with
     * 'Specific Row - Cancel', you could either pass
     * i13nLabel="Specific Row - Cancel"
     * or i13nModel={{ label: 'Specific Row - Cancel' }}. The action: 'click'
     * field will still be applied in the final i13nModel in either scenario.
     * Order of label precendence:
     * 1. i13nLabel
     * 2. i13nModel.label
     * 3. If the Link child is text, we will default the label to this text.
     * The third option will be the most common scenario.
     */
    i13nModel: PropTypes.object,
    className: PropTypes.string
    /**
     * Any other props are passed straight through to the child
     * See list of props react-router takes here:
     * https://github.com/ReactTraining/react-router/blob/v2.8.1/docs/API.md#link
     */
    //
  };

  static defaultProps = {
    newTab: true,
    className: ''
  };

  render() {
    /**
     * Order of label precendence:
     * 1. Will be i13nLabel if it is passed in.
     * 2. Will be set to i13nModel.label if passed in.
     * 3. If the Link child is text, we will default the label to this text.
     * The third option of defaulting to the Link text will be the most
     * common scenario.
     */
    const i13nModel = R.merge({ action: 'click' }, this.props.i13nModel);

    if (this.props.i13nLabel) {
      i13nModel.label = this.props.i13nLabel;
    }

    if (!i13nModel.label) {
      const childIsText = typeof this.props.children === 'string';
      i13nModel.label = childIsText ? this.props.children : 'Link';
    }

    const childProps = {
      className: `standard-link ${
        this.props.disabled ? 'disabled-link' : 'standard-link__active-link'
      } ${this.props.className}`,
      i13nModel,
      'data-testid': `${this.props.testid}`,
      ...R.omit(BLACKLIST_TO_CHILDREN_PROPS, { ...this.props }),
      ...{ onClick: this.props.disabled ? null : this.props.onClick }
    };

    // external link
    if (this.props.href) {
      return (
        <I13nAnchor
          {...childProps}
          target={this.props.newTab ? '_blank' : ''}
          rel={this.props.newTab ? 'noopener noreferrer' : ''}
        >
          {this.props.children}
        </I13nAnchor>
      );
    }

    // internal link route
    if (this.props.to) {
      return <I13nLink {...childProps}>{this.props.children}</I13nLink>;
    }

    // If neither href or to props passed, just triggers onClick.
    // Though not technically a link, we want to support this scenario and
    // keep it as an anchor tag to help with QA grabbing it
    // (they'll expect an anchor tag) and to simplify Mixpanel integration.
    return <I13nAnchor {...childProps}>{this.props.children}</I13nAnchor>;
  }
}

export default Link;
